/**
 * JS client library for detecting if a user has been served a
 * one-time promotion of any kind.
 *
 * Corresponds to PromoRecord in promo_record_model.py on the server side.
 */
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

// TODO: use localstorage or a more apollo-friendly approach?
/**
 * Cache of promo data.
 */
const cache_: Record<string, boolean> = {};

export function _internalAddPromoToCache(promoName: string) {
    cache_[promoName] = true;
}

export const HAS_USER_SEEN_PROMO_QUERY: GqlOperation<Data, Variables> = gqlOp(
    gql`
        query getPromoForUser($promoName: String!) {
            user {
                id
                promotion(promoName: $promoName)
            }
        }
    `,
);

type Data = {
    user: {
        id: string;
        promotion: boolean;
    };
};

type Variables = {
    promoName: string;
};

/**
 * Asynchronously checks to see if a user has been served a
 * particular promo.
 * @param {string} promoName The name of the promo to check.
 * @param {Function} callback A callback that accepts a single boolean
 *     indicating whether or not the user has seen the promo.
 * @param {Object} context Optional 'this' context to invoke the callback with.
 * @param {boolean} defaultValue Optional default result to return in the case
 *     of poor network connectivity and an uncached response.
 */
export default function hasUserSeen(
    gqlClient: ApolloGqlClient,
    promoName: string,
    callback: (hasSeen: boolean) => unknown,
    context?: any,
    defaultValue?: boolean,
) {
    if (promoName in cache_) {
        callback.call(context, cache_[promoName]);
        return;
    }

    const variables = {
        promoName: promoName,
    } as const;

    gqlClient
        .query(HAS_USER_SEEN_PROMO_QUERY, {
            variables,
            fetchPolicy: "network-only",
        })
        .then((result) => {
            // Mark the response, but if the server indicated that the
            // promo hasn't been seen, be sure to check if the promo was
            // marked as seen locally between the time the request started
            // and ended.
            const hasSeen = result.data?.user.promotion || !!cache_[promoName];
            cache_[promoName] = hasSeen;
            callback.call(context, hasSeen);
        })
        .catch((e) => {
            // If a default value was not provided and the promo is still
            // not in the cache, then err on the side of safety and avoid
            // showing promos when connectivity is flaky.
            const hasSeen =
                cache_[promoName] ??
                (defaultValue == null ? true : defaultValue);

            callback.call(context, hasSeen);
        });
}
